import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TRANSITION_MINIMUM_DURATION } from '@/player-config/src/types/RemotionPlayerConfigTypes/Animations/Transitions/Transitions.constants';
import RangeSlider from '@/components/atoms/RangeSlider';
import { classnames, debounce } from '@/libs/utils';
import useAnalytics from '@/hooks/useAnalytics';

export const TransitionDurationSlider = ({
  value,
  onChange,
  disabled,
  maxDuration
}: {
  value: number;
  onChange: (value: number) => void;
  disabled: boolean;
  maxDuration: number;
}) => {
  const [localDurationValue, setLocalDurationValue] = useState(value);
  const { trackTransitionDurationUpdateSource } = useAnalytics();

  const localDurationValueRef = useRef(localDurationValue);
  const configDurationValueRef = useRef(value);

  localDurationValueRef.current = localDurationValue;
  configDurationValueRef.current = value;

  const updateInPlayerConfig = useMemo(() => {
    const debouncedOnChange = debounce((newValue: number, source: string) => {
      trackTransitionDurationUpdateSource({ source });
      onChange(newValue);
    });
    return debouncedOnChange;
  }, [onChange, trackTransitionDurationUpdateSource]);

  const onSliderUpdate = useCallback(
    (newDuration: number) => {
      setLocalDurationValue(newDuration);
      updateInPlayerConfig(newDuration, 'SliderUpdateDebounced');
    },
    [updateInPlayerConfig]
  );

  const onInputUpdate = useCallback((temporaryValue: number) => {
    setLocalDurationValue(temporaryValue);
  }, []);

  const onInputBlur = useCallback(
    (newValue: number, source: string) => {
      const boundedValue = Math.min(Math.max(newValue, TRANSITION_MINIMUM_DURATION), maxDuration);
      updateInPlayerConfig(boundedValue, source);
      setLocalDurationValue(boundedValue);
    },
    [updateInPlayerConfig, maxDuration]
  );

  const currentInputBlur = useRef<(newValue: number, source: string) => void>(onInputBlur);
  currentInputBlur.current = onInputBlur;

  useEffect(() => {
    setLocalDurationValue(value);
  }, [value]);

  useEffect(() => {
    return () => {
      if (localDurationValueRef.current !== configDurationValueRef.current) {
        currentInputBlur.current(localDurationValueRef.current, 'TransitionMenuUnmount');
      }
    };
  }, []);

  return (
    <div className="flex flex-col">
      <div
        className={classnames(
          'mb-1 mt-2.5 select-none text-xs font-medium text-[#A5A9AD]',
          disabled ? 'opacity-50' : ''
        )}
      >
        Duration
      </div>
      <div className="flex flex-1 gap-2">
        <div className="flex flex-1 flex-col justify-center">
          <RangeSlider
            id="transition-duration"
            min={TRANSITION_MINIMUM_DURATION}
            max={maxDuration}
            step={1}
            value={localDurationValue}
            onChange={onSliderUpdate}
            disabled={disabled}
          />
        </div>
        <div className={classnames('flex items-center rounded-md bg-gray-100 text-xs', disabled ? 'opacity-50' : '')}>
          <input
            type="number"
            min={TRANSITION_MINIMUM_DURATION}
            max={maxDuration}
            value={localDurationValue}
            onChange={e => onInputUpdate(Number(e.target.value))}
            onBlur={() => onInputBlur(localDurationValue, 'InputBlur')}
            disabled={disabled}
            className={classnames(
              'w-10 rounded-md border-transparent bg-gray-100 p-1 text-end text-xs focus:border-transparent focus:outline-none focus:ring-0'
            )}
          />
          <div className="mr-1.5 text-xs text-[#444444] opacity-30">ms</div>
        </div>
      </div>
    </div>
  );
};
